'use client';
import React, { useEffect, useState } from 'react';
import TopNav from '../components/TopNav';
import MarketTicker from '../components/MarketTicker';
import BreakingNewsBar from '../components/BreakingNewsBar';
import FooterBar from '../components/FooterBar';
import { TrendingUp, TrendingDown, ArrowRight } from 'lucide-react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

interface CoinQuote {
  id: number;
  name: string;
  symbol: string;
  price: number;
  change24h: number;
  marketCap: number;
  volume24h: number;
}

interface GlobalMetrics {
  totalMarketCap: number;
  totalVolume24h: number;
  btcDominance: number;
  activeCryptocurrencies: number;
}

const COIN_META: Record<string, { iconBg: string; icon: string }> = {
  BTC: { iconBg: '#F7931A', icon: '₿' },
  ETH: { iconBg: '#627EEA', icon: 'Ξ' },
  SOL: { iconBg: '#9945FF', icon: '◎' },
  BNB: { iconBg: '#F3BA2F', icon: 'B' },
  XRP: { iconBg: '#00AAE4', icon: 'X' },
  ADA: { iconBg: '#0033AD', icon: 'A' },
  AVAX: { iconBg: '#E84142', icon: 'A' },
  DOGE: { iconBg: '#C2A633', icon: 'Ð' },
};

const TABLE_SYMBOLS = ['BTC', 'ETH', 'SOL', 'BNB', 'XRP', 'ADA', 'AVAX', 'DOGE'];

function formatPrice(price: number): string {
  if (price >= 1000) return '$' + price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  if (price >= 1) return '$' + price.toFixed(2);
  if (price >= 0.01) return '$' + price.toFixed(4);
  return '$' + price.toFixed(8);
}

function formatChange(change: number): string {
  const sign = change >= 0 ? '+' : '';
  return `${sign}${change.toFixed(2)}%`;
}

function formatLargeNum(n: number): string {
  if (n >= 1e12) return '$' + (n / 1e12).toFixed(2) + 'T';
  if (n >= 1e9) return '$' + (n / 1e9).toFixed(1) + 'B';
  if (n >= 1e6) return '$' + (n / 1e6).toFixed(1) + 'M';
  return '$' + n.toFixed(0);
}

const cryptoNews = [
  { id: 'cn-1', slug: 'bitcoin-etf-inflows-record-1-2b-single-day', title: 'Bitcoin ETF Inflows Hit Record $1.2B in Single Day', time: '2h ago', category: 'Bitcoin', image: "https://images.unsplash.com/photo-1667808929628-38fc8a48fa14", imageAlt: 'Bitcoin coin on dark background' },
  { id: 'cn-2', slug: 'ethereum-layer2-ecosystem-surpasses-50b-tvl', title: 'Ethereum Layer 2 Ecosystem Surpasses $50B TVL', time: '4h ago', category: 'Ethereum', image: "https://images.unsplash.com/photo-1666944073140-6bb0f0c5209e", imageAlt: 'Ethereum logo glowing blue' },
  { id: 'cn-3', slug: 'solana-meme-coin-season-drives-network-activity-ath', title: 'Solana Meme Coin Season Drives Network Activity to ATH', time: '6h ago', category: 'Altcoins', image: "https://images.unsplash.com/photo-1727381361657-6593a8033c83", imageAlt: 'Crypto trading chart green candles' },
];

export default function CryptoPage() {
  const [coins, setCoins] = useState<CoinQuote[]>([]);
  const [globalMetrics, setGlobalMetrics] = useState<GlobalMetrics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const res = await fetch('/api/crypto/market-data');
        if (!res.ok) return;
        const json = await res.json();
        setCoins(json.coins ?? []);
        setGlobalMetrics(json.globalMetrics ?? null);
      } catch {
        // keep static fallback
      } finally {
        setLoading(false);
      }
    }
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);

  const cryptoCoins = TABLE_SYMBOLS.map((sym) => {
    const coin = coins.find((c) => c.symbol === sym);
    const meta = COIN_META[sym] ?? { iconBg: '#888', icon: sym[0] };
    if (!coin) return null;
    return {
      id: `c-${sym.toLowerCase()}`,
      name: coin.name,
      symbol: sym,
      price: formatPrice(coin.price),
      change: formatChange(coin.change24h),
      positive: coin.change24h >= 0,
      mcap: formatLargeNum(coin.marketCap),
      vol: formatLargeNum(coin.volume24h),
      iconBg: meta.iconBg,
      icon: meta.icon,
    };
  }).filter(Boolean) as NonNullable<ReturnType<typeof Array.prototype.map>[number]>[];

  const statsRow = [
    { label: 'Total Market Cap', value: globalMetrics ? formatLargeNum(globalMetrics.totalMarketCap) : '$2.41T', change: '+1.8%', positive: true },
    { label: '24h Volume', value: globalMetrics ? formatLargeNum(globalMetrics.totalVolume24h) : '$89.2B', change: '+5.2%', positive: true },
    { label: 'BTC Dominance', value: globalMetrics ? globalMetrics.btcDominance.toFixed(1) + '%' : '55.6%', change: '-0.3%', positive: false },
    { label: 'Active Cryptos', value: globalMetrics ? globalMetrics.activeCryptocurrencies.toLocaleString() : '22,847', change: '+124', positive: true },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <TopNav />
      <MarketTicker />
      <BreakingNewsBar />
      <main className="w-full max-w-screen-2xl mx-auto px-4 lg:px-6 xl:px-8 2xl:px-10 py-6">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-primary text-xs font-bold uppercase tracking-widest">Live Markets</span>
          </div>
          <h1 className="text-3xl font-black text-foreground">Crypto Markets</h1>
          <p className="text-muted-foreground text-sm mt-1">Real-time prices, market caps, and volume data for top cryptocurrencies.</p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {statsRow?.map((stat) =>
            <div key={stat?.label} className="bg-card border border-border rounded-lg p-4">
              <p className="text-xs text-muted-foreground mb-1">{stat?.label}</p>
              <p className="text-xl font-black text-foreground tabular-nums">{stat?.value}</p>
              <p className={`text-xs font-semibold mt-0.5 ${stat?.positive ? 'text-crypto-green' : 'text-crypto-red'}`}>{stat?.change}</p>
            </div>
          )}
        </div>

        {/* Coin Table */}
        <div className="bg-card border border-border rounded-lg overflow-hidden mb-6">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h2 className="text-base font-semibold text-foreground">All Cryptocurrencies</h2>
            <span className="text-xs text-muted-foreground">Updated live</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left px-4 py-3 text-xs text-muted-foreground font-medium">#</th>
                  <th className="text-left px-4 py-3 text-xs text-muted-foreground font-medium">Coin</th>
                  <th className="text-right px-4 py-3 text-xs text-muted-foreground font-medium">Price</th>
                  <th className="text-right px-4 py-3 text-xs text-muted-foreground font-medium">24H %</th>
                  <th className="text-right px-4 py-3 text-xs text-muted-foreground font-medium hidden sm:table-cell">Market Cap</th>
                  <th className="text-right px-4 py-3 text-xs text-muted-foreground font-medium hidden md:table-cell">Volume (24H)</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-xs text-muted-foreground">Loading live data…</td></tr>
                ) : cryptoCoins.length > 0 ? cryptoCoins?.map((coin, idx) =>
                  <tr key={coin?.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors duration-150">
                    <td className="px-4 py-3 text-xs text-muted-foreground">{idx + 1}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: coin?.iconBg }}>
                          {coin?.icon}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-foreground">{coin?.name}</p>
                          <p className="text-[10px] text-muted-foreground font-mono">{coin?.symbol}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-semibold text-foreground tabular-nums font-mono">{coin?.price}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`flex items-center justify-end gap-0.5 text-sm font-semibold tabular-nums ${coin?.positive ? 'text-crypto-green' : 'text-crypto-red'}`}>
                        {coin?.positive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                        {coin?.change}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-muted-foreground hidden sm:table-cell tabular-nums">{coin?.mcap}</td>
                    <td className="px-4 py-3 text-right text-sm text-muted-foreground hidden md:table-cell tabular-nums">{coin?.vol}</td>
                  </tr>
                ) : (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-xs text-muted-foreground">No data available</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* News */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-foreground">Crypto News</h2>
            <Link href="/" className="flex items-center gap-1 text-xs text-crypto-green hover:text-crypto-green/80 font-medium">View All <ArrowRight size={12} /></Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {cryptoNews?.map((n) =>
              <Link key={n?.id} href={`/blog/${n?.slug}`} className="bg-card border border-border rounded-lg overflow-hidden card-hover block">
                <div className="relative h-36 bg-muted">
                  <AppImage src={n?.image} alt={n?.imageAlt} fill className="object-cover" sizes="(max-width: 640px) 100vw, 33vw" />
                  <span className="absolute top-2 left-2 bg-primary text-white text-[10px] font-bold px-2 py-0.5 rounded uppercase">{n?.category}</span>
                </div>
                <div className="p-3">
                  <p className="text-xs text-muted-foreground mb-1">{n?.time}</p>
                  <h3 className="text-sm font-semibold text-foreground leading-snug line-clamp-2">{n?.title}</h3>
                  <span className="flex items-center gap-1 text-xs text-primary font-medium mt-2">Read More <ArrowRight size={11} /></span>
                </div>
              </Link>
            )}
          </div>
        </div>
      </main>
      <FooterBar />
    </div>
  );
}